/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  TransitTrackerPeekView,
  TrackingStatus,
  Header,
  ELDStatus,
  CardContent,
  Logo,
  CardRightSide,
  TruckerToolsLogo,
  FourKitesLogo,
} from './transit-tracker-connection.style';
import { TRANSIT_APP_STATE, VISIBILITY_APP_NAME } from '../../../../../constants';
import ConfirmationModal from '../../../common/modals/confirmation-modal';
import truckerToolsLogo from '../../../../../assets/truckertools-logo.svg';
import fourKitesLogo from '../../../../../assets/fourkites-logo.svg';
import loadShape from '../../../../../shapes/load';
import Link from '../../../common/link';
import { updateLoadVisibilityAppStatus } from '../../../../../utils/api/load';
import { showSuccessMessage, showErrorMessage, logger } from '../../../../../utils/common';
import { formatErrorResponse } from '../../../../../utils/mappers';

/**
 * The transit tracker component
 * @param {object} params - Object containing the props for this component
 * @param {object} params.load - The specified load
 * @param {func} params.refetchLoad - Handles refetching load ata
 * @return {element}
 * Todo: pull the renderLogo variable from the API
 */
const TransitTrackerConnection = ({ load, refetchLoad }) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [updateStatusProcessing, setUpdateStatusProcessing] = useState(false);

  const hasConnErr = load.visibilityAppStatus !== TRANSIT_APP_STATE.tracking
    && load.visibilityAppStatus !== TRANSIT_APP_STATE.requestSent;

  const handleMarkRefused = () => {
    setShowConfirmation(true);
  };

  const handleConfirmation = () => {
    setUpdateStatusProcessing(true);

    updateLoadVisibilityAppStatus({
      loadId: load.loadId,
      newVisibilityAppStatus: TRANSIT_APP_STATE.driverRefused,
    }).then(() => {
      showSuccessMessage(`Visibility App Connection Status manually updated to ${TRANSIT_APP_STATE.driverRefused} on Load #${load.billOfLadingNumber}`);
      refetchLoad({ visibilityAppStatus: TRANSIT_APP_STATE.driverRefused });
    }).catch((error) => {
      logger.error(formatErrorResponse(error));
      showErrorMessage('Something went wrong. The service is currently unavailable. Please try again later.');
    }).finally(() => {
      setShowConfirmation(false);
      setUpdateStatusProcessing(false);
    });
  };

  /**
   * Renders a logo for the appropriate visibility app
   * @param {string} action - Action to render
   * @return {element} - View for appropriate action
   */
  const renderLogo = (action) => {
    switch (action) {
      case VISIBILITY_APP_NAME.truckerTools:
        return (
          <TruckerToolsLogo src={truckerToolsLogo} />
        );
      case VISIBILITY_APP_NAME.fourKites:
        return (
          <FourKitesLogo src={fourKitesLogo} />
        );
      default:
        return <Logo />;
    }
  };
  /**
   * Renders a given action
   * @param {string} action - Action to render
   * @return {element} - View for appropriate action
   */
  const renderAction = (action) => {
    switch (action) {
      case TRANSIT_APP_STATE.tracking:
        return (<TrackingStatus>Tracking</TrackingStatus>);
      case TRANSIT_APP_STATE.driverRefused:
        return (<TrackingStatus>Driver Refused</TrackingStatus>);
      case TRANSIT_APP_STATE.requestNotSent:
        return (<TrackingStatus>Request Not Sent</TrackingStatus>);
      case TRANSIT_APP_STATE.lostConnection:
        return (<TrackingStatus>Lost Connection</TrackingStatus>);
      case TRANSIT_APP_STATE.installedTrackingNotStarted:
        return (<TrackingStatus>Installed, Tracking Not Started</TrackingStatus>);
      case TRANSIT_APP_STATE.requestSent:
        return (
          <div>
            <TrackingStatus>Request Sent</TrackingStatus>
            {load.visibilityAppName === VISIBILITY_APP_NAME.fourKites && (
              <Link onClick={handleMarkRefused} text="(Mark as Driver Refused)" />
            )}
          </div>
        );
      default:
        return (<TrackingStatus>{action}</TrackingStatus>);
    }
  };

  const eldStatus = `${load.driverInformation?.trailerNumber ? 'Trailer' : 'Truck'} ELD Integrated`;

  return (
    <TransitTrackerPeekView hasErr={hasConnErr}>
      <CardContent>
        <Header>Visibility App Connection</Header>
        {renderAction(load.visibilityAppStatus)}
        {load.isELD && <ELDStatus>{eldStatus}</ELDStatus>}
      </CardContent>
      <CardRightSide>
        {renderLogo(load.visibilityAppName)}
      </CardRightSide>
      <ConfirmationModal
        title="Mark as Driver Refused"
        text="Please confirm the driver refused connection to the FourKites Visibility App."
        isOpen={showConfirmation}
        isLoading={updateStatusProcessing}
        onClose={() => setShowConfirmation(false)}
        onConfirm={() => handleConfirmation(false)}
        onCancel={() => setShowConfirmation(false)}
      />
    </TransitTrackerPeekView>
  );
};

TransitTrackerConnection.propTypes = {
  load: loadShape,
  refetchLoad: PropTypes.func.isRequired,
};

TransitTrackerConnection.defaultProps = {
  load: null,
};

export default TransitTrackerConnection;
